import React, { useState, useEffect } from "react";

import axios from "axios";
import moment from "moment";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { FileDownload as FileDownloadIcon } from "@mui/icons-material";

import { useGradeSections } from "api/grade_sections";
import { useGetMonthlyReport } from "api/report/attendances";
import { ExcelPreview } from "./ExcelPreview";
import { useI18n } from "i18n";

interface Grade {
  id: number;
  name: string;
}

interface Section {
  id: number;
  name: string;
}

// ファイル名抽出のヘルパー関数
const extractFileNameFromResponse = (
  response: any,
): string => {
  try {
    // Content-Dispositionヘッダーからファイル名を抽出
    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];

    if (contentDisposition) {
      // filename*=UTF-8''encoded_filename 形式（国際化対応）
      const fileNameStarMatch = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)/i
      );
      if (fileNameStarMatch && fileNameStarMatch[1]) {
        return decodeURIComponent(fileNameStarMatch[1]);
      }

      // filename="filename" または filename=filename 形式
      const fileNameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i
      );
      if (fileNameMatch && fileNameMatch[1]) {
        return fileNameMatch[1].replace(/['"]/g, "");
      }
    }
  } catch (error) {
    console.warn(
      "Failed to extract filename from Content-Disposition header:",
      error
    );
  }

  const defaultFileName = "Attendance_Report.xlsx";
  return defaultFileName;
};

const Page = () => {
  const { t } = useI18n();
  // State for form data
  const [selectedGradeId, setSelectedGradeId] = useState<number | "">("");
  const [selectedSectionId, setSelectedSectionId] = useState<number | "">("");
  const [selectedYear, setSelectedYear] = useState<number>(moment().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(
    moment().month() + 1
  );

  // State for data
  const [grades, setGrades] = useState<Grade[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [error, setError] = useState<string>("");

  // State for Excel preview
  const [excelBlob, setExcelBlob] = useState<Blob | null>(null);
  const [excelFileName, setExcelFileName] = useState<string>("");
  const [previewOpen, setPreviewOpen] = useState(false);

  // API hooks
  const { loading: loadingGradeSections, getGradeSections } =
    useGradeSections();
  const { loading: generatingReport, service: getMonthlyReport } =
    useGetMonthlyReport();

  // Load grades and sections on component mount
  useEffect(() => {
    const loadGradeSections = async () => {
      try {
        const response = await getGradeSections();
        if (response.data.result === "success") {
          const { grades, sections } = response.data;
          setGrades(grades || []);
          setSections(sections || []);
        }
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error("Failed to load grades and sections:", error);
        setError(t("report.loadGradeSectionsError"));
      }
    };

    loadGradeSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter sections based on selected grade (if needed)
  const filteredSections = sections;

  // Generate year options (current year and previous years)
  const yearOptions: number[] = [];
  const currentYear = moment().year();
  for (let i = 0; i < 5; i++) {
    yearOptions.push(currentYear - i);
  }

  // Generate month options
  const monthOptions = [
    { value: 1, label: `01- ${t("month.january")}` },
    { value: 2, label: `02- ${t("month.february")}` },
    { value: 3, label: `03- ${t("month.march")}` },
    { value: 4, label: `04- ${t("month.april")}` },
    { value: 5, label: `05- ${t("month.may")}` },
    { value: 6, label: `06- ${t("month.june")}` },
    { value: 7, label: `07- ${t("month.july")}` },
    { value: 8, label: `08- ${t("month.august")}` },
    { value: 9, label: `09- ${t("month.september")}` },
    { value: 10, label: `10- ${t("month.october")}` },
    { value: 11, label: `11- ${t("month.november")}` },
    { value: 12, label: `12- ${t("month.december")}` },
  ];

  const handleGradeChange = (event: SelectChangeEvent<number>) => {
    setSelectedGradeId(event.target.value as number);
    setError("");
  };

  const handleSectionChange = (event: SelectChangeEvent<number>) => {
    setSelectedSectionId(event.target.value as number);
    setError("");
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(event.target.value as number);
  };

  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setSelectedMonth(event.target.value as number);
  };

  const handleGenerateReport = async () => {
    if (!selectedGradeId || !selectedSectionId) {
      setError(t("report.requiredGradeSection"));
      return;
    }

    try {
      const response = await getMonthlyReport({
        gradeId: selectedGradeId as number,
        sectionId: selectedSectionId as number,
        year: selectedYear,
        month: selectedMonth,
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = extractFileNameFromResponse(response);

      setExcelBlob(blob);
      setExcelFileName(fileName);
      setPreviewOpen(true);
      setError("");
    } catch (error) {
      console.error("Failed to generate report:", error);
      setError(t("report.generateError"));
    }
  };

  const isFormValid =
    selectedGradeId && selectedSectionId && selectedYear && selectedMonth;

  return (
    <React.Fragment>
      <Box>
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 4 }}>
            <Typography
              variant="h1"
              gutterBottom
              sx={{ fontSize: "24px", fontWeight: "bold", mb: 0 }}
            >
              {t("report.attendanceTitle")}
            </Typography>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t("report.generateMonthly")}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="grade-select-label">
                      {t("field.grade")}
                    </InputLabel>
                    <Select
                      labelId="grade-select-label"
                      value={selectedGradeId}
                      label={t("field.grade")}
                      onChange={handleGradeChange}
                      disabled={loadingGradeSections}
                    >
                      {loadingGradeSections ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          {t("common.loading")}
                        </MenuItem>
                      ) : (
                        grades.map((grade) => (
                          <MenuItem key={grade.id} value={grade.id}>
                            {grade.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="section-select-label">
                      {t("field.section")}
                    </InputLabel>
                    <Select
                      labelId="section-select-label"
                      value={selectedSectionId}
                      label={t("field.section")}
                      onChange={handleSectionChange}
                      disabled={loadingGradeSections}
                    >
                      {loadingGradeSections ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          {t("common.loading")}
                        </MenuItem>
                      ) : (
                        filteredSections.map((section) => (
                          <MenuItem key={section.id} value={section.id}>
                            {section.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="year-select-label">
                      {t("field.year")}
                    </InputLabel>
                    <Select
                      labelId="year-select-label"
                      value={selectedYear}
                      label={t("field.year")}
                      onChange={handleYearChange}
                    >
                      {yearOptions.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="month-select-label">
                      {t("field.month")}
                    </InputLabel>
                    <Select
                      labelId="month-select-label"
                      value={selectedMonth}
                      label={t("field.month")}
                      onChange={handleMonthChange}
                    >
                      {monthOptions.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    generatingReport ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <FileDownloadIcon />
                    )
                  }
                  onClick={handleGenerateReport}
                  disabled={!isFormValid || generatingReport}
                  sx={{ minWidth: 200 }}
                >
                  {generatingReport ? t("common.generating") : t("report.generate")}
                </Button>
              </Box>

              {selectedGradeId && selectedSectionId && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t("report.selected")}</strong>{" "}
                    {grades.find((g) => g.id === selectedGradeId)?.name} -{" "}
                    {
                      filteredSections.find((s) => s.id === selectedSectionId)
                        ?.name
                    }{" "}
                    |{" "}
                    {monthOptions.find((m) => m.value === selectedMonth)?.label}{" "}
                    {selectedYear}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>

      <ExcelPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        excelBlob={excelBlob}
        fileName={excelFileName}
      />
    </React.Fragment>
  );
};

export default Page;
